import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, Paper, Divider, alpha, Button, Tabs, Tab,
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import {
  AccessTime as ClockIcon,
  Login as PunchInIcon,
  Logout as PunchOutIcon,
  Description as DocIcon,
  People as ContactsIcon,
  Business as AccountsIcon,
  Work as JobsIcon,
  Timeline as ActivityIcon,
  TrendingUp as TrendingIcon,
  CalendarMonth as CalendarIcon,
  Circle as StatusIcon,
  Speed as ProductivityIcon,
  DateRange as WeekIcon,
  CalendarToday as MonthIcon
} from '@mui/icons-material';
import api from '../../utils/api';

const PunchCard = () => {
  const [loading, setLoading] = useState(true);
  const [dailySummary, setDailySummary] = useState(null);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fetchData = useCallback(async () => {
    try {
      const [dailyRes, weeklyRes, monthlyRes, statusRes] = await Promise.all([
        api.get('/punchcard/daily-summary'),
        api.get('/punchcard/weekly-summary'),
        api.get(`/punchcard/monthly-summary?month=${selectedMonth}&year=${selectedYear}`),
        api.get('/punchcard/status')
      ]);
      setDailySummary(dailyRes.data.data);
      setWeeklySummary(weeklyRes.data.data);
      setMonthlySummary(monthlyRes.data.data);
      setCurrentStatus(statusRes.data.data);
    } catch (error) {
      console.error('Failed to fetch punch card data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    fetchData();
    // Refresh every minute
    const interval = setInterval(fetchData, 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Listen for punch status changes from other components (like DigitalClock header)
  useEffect(() => {
    const handlePunchStatusChange = () => {
      fetchData();
    };
    window.addEventListener('punchStatusChanged', handlePunchStatusChange);
    return () => window.removeEventListener('punchStatusChanged', handlePunchStatusChange);
  }, [fetchData]);

  const handlePunchIn = async () => {
    try {
      await api.post('/punchcard/punch-in');
      fetchData();
      // Dispatch event to sync other components
      window.dispatchEvent(new CustomEvent('punchStatusChanged', { detail: { action: 'punch-in' } }));
    } catch (error) {
      console.error('Punch in error:', error);
    }
  };

  const handlePunchOut = async () => {
    try {
      await api.post('/punchcard/punch-out');
      fetchData();
      // Dispatch event to sync other components
      window.dispatchEvent(new CustomEvent('punchStatusChanged', { detail: { action: 'punch-out' } }));
    } catch (error) {
      console.error('Punch out error:', error);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatMinutes = (minutes) => {
    if (!minutes) return '0h 0m';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };

  const formatDuration = (hours) => {
    if (!hours) return '-';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'completed': return 'default';
      case 'idle': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Working';
      case 'completed': return 'Completed';
      case 'idle': return 'Idle';
      case 'auto_closed': return 'Auto Closed';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const sessions = dailySummary?.sessions || [];
  const summary = dailySummary?.summary || {};
  const activityByModule = dailySummary?.activityByModule || [];
  const recentActivities = dailySummary?.recentActivities || [];
  const totalWorkingMinutes = dailySummary?.totalWorkingMinutes || 0;
  const isOnline = currentStatus?.isOnline;
  const isIdle = currentStatus?.isIdle;
  
  // Productivity calculations
  const dailyProductivity = dailySummary?.productivityPercent || 0;
  const weeklyProductivity = weeklySummary?.productivityPercent || 0;
  const monthlyProductivity = monthlySummary?.productivityPercent || 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ClockIcon sx={{ fontSize: 32 }} />
          My Punch Card
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your daily activity tracker and work summary
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Current Status Card */}
        <Grid item xs={12}>
          <Card sx={{ 
            background: (theme) => isOnline 
              ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.light, 0.05)} 100%)`,
            border: '1px solid',
            borderColor: isOnline ? 'success.light' : 'warning.light'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <StatusIcon
                      sx={{
                        fontSize: 24,
                        color: isOnline ? 'success.main' : 'warning.main',
                        animation: isOnline ? 'pulse 2s infinite' : 'none',
                        '@keyframes pulse': {
                          '0%': { opacity: 1 },
                          '50%': { opacity: 0.5 },
                          '100%': { opacity: 1 }
                        }
                      }}
                    />
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {isOnline ? 'Currently Online' : 'Currently Offline'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {isOnline 
                          ? `Active for ${currentStatus?.minutesSinceLastActivity || 0} min since last activity`
                          : 'Click Punch In to start tracking'
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {formatMinutes(totalWorkingMinutes)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Total Working Time Today</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                  {isIdle ? (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<PunchInIcon />}
                      onClick={handlePunchIn}
                      sx={{
                        bgcolor: '#22c55e',
                        '&:hover': { bgcolor: '#16a34a' },
                        px: 4,
                        py: 1.5,
                        color: 'white',
                        fontWeight: 600
                      }}
                    >
                      Punch In
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<PunchOutIcon />}
                      onClick={handlePunchOut}
                      sx={{
                        bgcolor: '#ef4444',
                        '&:hover': { bgcolor: '#dc2626' },
                        px: 4,
                        py: 1.5,
                        color: 'white',
                        fontWeight: 600
                      }}
                    >
                      Punch Out
                    </Button>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Today's Sessions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon />
                Today's Sessions
              </Typography>
              {sessions.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Started</TableCell>
                        <TableCell>Ended</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sessions.map((session, index) => (
                        <TableRow key={session.id}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{formatTime(session.punch_in)}</TableCell>
                          <TableCell>{formatTime(session.punch_out)}</TableCell>
                          <TableCell>{session.total_hours ? formatDuration(session.total_hours) : '-'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={getStatusLabel(session.status)}
                              color={getStatusColor(session.status)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No sessions recorded today. Click "Punch In" to start.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Today's Activity Summary with Productivity */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingIcon />
                Today's Summary
              </Typography>

              {/* Productivity Gauge */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ProductivityIcon sx={{ color: dailyProductivity >= 75 ? 'success.main' : dailyProductivity >= 50 ? 'warning.main' : 'error.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Daily Productivity
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: dailyProductivity >= 75 ? 'success.main' : dailyProductivity >= 50 ? 'warning.main' : 'error.main' }}>
                    {dailyProductivity}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={dailyProductivity}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      bgcolor: dailyProductivity >= 75 ? 'success.main' : dailyProductivity >= 50 ? 'warning.main' : 'error.main'
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Based on 8 hours workday ({formatMinutes(totalWorkingMinutes)} / 8h)
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <StatCard
                    icon={<ActivityIcon />}
                    label="Total Activities"
                    value={summary.totalActivities || 0}
                    color="#6366f1"
                  />
                </Grid>
                <Grid item xs={6}>
                  <StatCard
                    icon={<DocIcon />}
                    label="Resumes Uploaded"
                    value={summary.resumesUploaded || 0}
                    color="#ec4899"
                  />
                </Grid>
                <Grid item xs={6}>
                  <StatCard
                    icon={<ContactsIcon />}
                    label="Contacts Created"
                    value={summary.contactsCreated || 0}
                    color="#10b981"
                  />
                </Grid>
                <Grid item xs={6}>
                  <StatCard
                    icon={<AccountsIcon />}
                    label="Accounts Created"
                    value={summary.accountsCreated || 0}
                    color="#f59e0b"
                  />
                </Grid>
                <Grid item xs={12}>
                  <StatCard
                    icon={<JobsIcon />}
                    label="Jobs Handled"
                    value={summary.jobsHandled || 0}
                    color="#3b82f6"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Activity by Module */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Activity by Module
              </Typography>
              {activityByModule.length > 0 ? (
                <Box>
                  {activityByModule.map((item, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {item.module}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.count}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min((parseInt(item.count) / Math.max(...activityByModule.map(a => parseInt(a.count)))) * 100, 100)}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No activities recorded yet today
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Weekly Summary with Productivity */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <WeekIcon />
                This Week's Summary
              </Typography>
              {weeklySummary && (
                <Box>
                  {/* Weekly Productivity Gauge */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Weekly Productivity
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: weeklyProductivity >= 75 ? 'success.main' : weeklyProductivity >= 50 ? 'warning.main' : 'error.main' }}>
                        {weeklyProductivity}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={weeklyProductivity}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          bgcolor: weeklyProductivity >= 75 ? 'success.main' : weeklyProductivity >= 50 ? 'warning.main' : 'error.main'
                        }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Based on 40 hours/week ({weeklySummary.totalHours}h / 40h)
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {weeklySummary.daysWorked}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Days Worked</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                          {weeklySummary.totalHours}h
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Hours</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                          {weeklySummary.totalActivities}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Activities</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Summary */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MonthIcon />
                  Monthly Summary
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Month</InputLabel>
                    <Select
                      value={selectedMonth}
                      label="Month"
                      onChange={(e) => setSelectedMonth(e.target.value)}
                    >
                      {[...Array(12)].map((_, i) => (
                        <MenuItem key={i + 1} value={i + 1}>
                          {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Year</InputLabel>
                    <Select
                      value={selectedYear}
                      label="Year"
                      onChange={(e) => setSelectedYear(e.target.value)}
                    >
                      {[2024, 2025, 2026].map((year) => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {monthlySummary && (
                <Grid container spacing={3}>
                  {/* Monthly Productivity */}
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        background: (theme) => monthlyProductivity >= 75
                          ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`
                          : monthlyProductivity >= 50
                          ? `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.light, 0.05)} 100%)`
                          : `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.light, 0.05)} 100%)`,
                        border: '1px solid',
                        borderColor: monthlyProductivity >= 75 ? 'success.light' : monthlyProductivity >= 50 ? 'warning.light' : 'error.light',
                        textAlign: 'center'
                      }}
                    >
                      <ProductivityIcon sx={{ fontSize: 48, color: monthlyProductivity >= 75 ? 'success.main' : monthlyProductivity >= 50 ? 'warning.main' : 'error.main', mb: 1 }} />
                      <Typography variant="h2" sx={{ fontWeight: 700, color: monthlyProductivity >= 75 ? 'success.main' : monthlyProductivity >= 50 ? 'warning.main' : 'error.main' }}>
                        {monthlyProductivity}%
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Monthly Productivity
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {monthlySummary.totalHours}h / {monthlySummary.expectedHours}h expected
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Monthly Stats */}
                  <Grid item xs={12} md={8}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {monthlySummary.daysWorked}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Days Worked</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
                            {monthlySummary.workingDaysInMonth}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Working Days</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                            {monthlySummary.totalHours}h
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Total Hours</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                            {monthlySummary.totalActivities}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Activities</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            Average Hours per Day: {monthlySummary.averageHoursPerDay}h
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, (parseFloat(monthlySummary.averageHoursPerDay) / 8) * 100)}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'grey.300'
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activities */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Recent Activities Today
              </Typography>
              {recentActivities.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        <TableCell>Module</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentActivities.map((activity) => (
                        <TableRow key={activity.id}>
                          <TableCell>
                            {new Date(activity.performed_at).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={activity.module}
                              size="small"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>
                            {activity.action}
                          </TableCell>
                          <TableCell>{activity.description}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No activities recorded yet today
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, color, fullWidth = false }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      bgcolor: alpha(color, 0.1),
      border: '1px solid',
      borderColor: alpha(color, 0.2),
      display: 'flex',
      alignItems: 'center',
      gap: 2
    }}
  >
    <Box sx={{ color }}>{icon}</Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}</Typography>
    </Box>
  </Box>
);

export default PunchCard;
